// The Spa Manager's cabinet: an Elettra KS714.30 (Kappa series, IP43, glass
// door; elettra.it, "Armadio IP43 con porta L700xH1400xP300"), 700 wide, 300
// deep, 1400 tall, 15/10 steel, RAL 7035, on its Kappa plinth (100 tall, sold
// apart: the KZA30/100 covers of a 300 deep cabinet). Its plan symbol and its stored 3D model
// (`model3d`, drawn by the generic builder, shared/partSolids.ts) as the
// built-in `builtin-spamanager` carries them: drawn front to +y, carried turned
// half round, its front to −y (shared/turnHalf.ts). Pure (test/partSolids.test.mjs).

import type {
  GeomEntity,
  Geometry,
  PartModel3d,
  PartSolid3d,
  Vec2,
} from './types';
import {
  turnGeometry,
  turnModel3d,
} from './turnHalf';

/** The cabinet's figures (mm): width, depth, height. */
export const KS714_SIZE: [number, number, number] = [700, 300, 1400];
/** Its plinth's height, under it; the Spa Manager stands on both. */
export const KS714_PLINTH = 100;
export const SPA_MANAGER_SIZE: [number, number, number] = [KS714_SIZE[0], KS714_SIZE[1], KS714_SIZE[2] + KS714_PLINTH];
/** RAL 7035, light grey. */
export const KS714_COLOUR = '#cbd0cc';

/** The steel sheet; the door's depth (frame and glass) at the front; its frame's
 *  stiles and rails; how far the cover plates stand behind it. */
const SHEET = 2;
const DOOR = 20;
const STILE = 40;
const RAIL = 60;
const PLATES_BACK = 40;
/** The front frame's uprights: the cabinet's usable width is 652 (700 − 2 × 24). */
const UPRIGHT = 24;
/** The cover plates' rows (from the floor): a blank plate, five rows of DIN
 *  devices (each a 150 plate with its 45 window on 24 modules, 432), a blank
 *  plate at the top. */
const PLATES_FROM = 100;
const ROW = 150;
const DIN_ROWS = 5;
const TOP_PLATE = 300;
const WINDOW_W = 432;
const WINDOW_H = 45;
/** The breakers stand this far through their window. */
const DEVICES_OUT = 12;

const box = (x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, role: string): PartSolid3d =>
  ({ kind: 'box', role, x0, x1, y0, y1, z0, z1 });

/** A solid raised by `dz`. */
function lift(s: PartSolid3d, dz: number): PartSolid3d {
  switch (s.kind) {
    case 'box':
    case 'prism': return { ...s, z0: s.z0 + dz, z1: s.z1 + dz };
    case 'cyl': return s.axis === 'z' ? { ...s, from: s.from + dz, to: s.to + dz } : { ...s, at: [s.at[0], s.at[1] + dz] };
    case 'hull': return { ...s, pts: s.pts.map(([x, y, z]) => [x, y, z + dz]) };
    case 'pipe': return { ...s, path: s.path.map(([x, y, z]) => [x, y, z + dz]) };
    case 'revolve': return { ...s, at: [s.at[0], s.at[1] + dz] };
  }
}

/** The plinth: a black post at each corner, a cover on each face between them
 *  (each face its own solid, as the shell's). */
const POST = 50;
function plinth(hw: number, hd: number): PartSolid3d[] {
  const out: PartSolid3d[] = [];
  for (const sx of [1, -1]) for (const sy of [1, -1]) out.push(box(sx > 0 ? hw - POST : -hw, sx > 0 ? hw : -hw + POST, sy > 0 ? hd - POST : -hd, sy > 0 ? hd : -hd + POST, 0, KS714_PLINTH, 'plinth.posts'));
  out.push(
    box(-hw + POST, hw - POST, hd - SHEET, hd, 0, KS714_PLINTH, 'plinth.front'),
    box(-hw + POST, hw - POST, -hd, -hd + SHEET, 0, KS714_PLINTH, 'plinth.back'),
    box(hw - SHEET, hw, -hd + POST, hd - POST, 0, KS714_PLINTH, 'plinth.side-l'),
    box(-hw, -hw + SHEET, -hd + POST, hd - POST, 0, KS714_PLINTH, 'plinth.side-r'),
  );
  return out;
}

/** The cabinet on its plinth as its part stores it, in the part's frame (x
 *  across its width, +x the side on your left as you face its door; y toward
 *  the door, its back at −d/2; z up from the floor). Each face of the shell is a sub-role of
 *  `body` (its own solid, so a view paints it in turn, in the copy's colour):
 *  the back, the sides, the base and the roof, the front frame and the door's
 *  frame; the glass see-through; the cover plates behind it with the DIN rows'
 *  breakers through their windows; the lever handle on the door's free edge
 *  (−x), the three hinges on its hinged one (+x), the lifting brackets on the
 *  roof (10 over it: 1410 is the data sheet's overall height); the plinth
 *  under it all. */
export function ks714Model3d(): PartModel3d {
  const [w, d, h] = KS714_SIZE, hw = w / 2, hd = d / 2;
  const shell = hd - DOOR;
  const plateY = shell - PLATES_BACK;
  const solids: PartSolid3d[] = [
    box(-hw, hw, -hd, -hd + SHEET, 0, h, 'body.back'),
    box(hw - SHEET, hw, -hd + SHEET, shell, 0, h, 'body.side-l'),
    box(-hw, -hw + SHEET, -hd + SHEET, shell, 0, h, 'body.side-r'),
    box(-hw + SHEET, hw - SHEET, -hd + SHEET, shell, 0, SHEET, 'body.base'),
    box(-hw + SHEET, hw - SHEET, -hd + SHEET, shell, h - SHEET, h, 'body.roof'),
    box(hw - UPRIGHT, hw - SHEET, shell - SHEET, shell, SHEET, h - SHEET, 'body.frame'),
    box(-hw + SHEET, -hw + UPRIGHT, shell - SHEET, shell, SHEET, h - SHEET, 'body.frame'),
    box(-hw + UPRIGHT, hw - UPRIGHT, shell - SHEET, shell, h - RAIL, h - SHEET, 'body.frame'),
    box(-hw + UPRIGHT, hw - UPRIGHT, shell - SHEET, shell, SHEET, RAIL, 'body.frame'),
  ];
  // The cover plates: blank at the bottom and the top, a window in each DIN row
  // with its breakers standing through it (their toggles a dark line).
  const px = hw - UPRIGHT - 6, plate = (z0: number, z1: number): PartSolid3d => box(-px, px, plateY - SHEET, plateY, z0, z1, 'plates');
  solids.push(plate(PLATES_FROM, PLATES_FROM + ROW));
  for (let i = 0; i < DIN_ROWS; i++) {
    const z0 = PLATES_FROM + ROW * (i + 1), zm = z0 + ROW / 2, ww = WINDOW_W / 2, wh = WINDOW_H / 2;
    solids.push(
      plate(z0, zm - wh), plate(zm + wh, z0 + ROW),
      box(-px, -ww, plateY - SHEET, plateY, zm - wh, zm + wh, 'plates'),
      box(ww, px, plateY - SHEET, plateY, zm - wh, zm + wh, 'plates'),
      box(-ww, ww, plateY - 30, plateY + DEVICES_OUT, zm - wh, zm + wh, 'devices'),
      box(-ww, ww, plateY + DEVICES_OUT, plateY + DEVICES_OUT + 4, zm - 4, zm + 4, 'toggles'),
    );
  }
  const topFrom = PLATES_FROM + ROW * (DIN_ROWS + 1);
  solids.push(plate(topFrom, topFrom + TOP_PLATE));
  // The door: its frame round the glass, the glass set in it.
  const dx = hw - SHEET, dz0 = SHEET, dz1 = h - SHEET;
  solids.push(
    box(dx - STILE, dx, shell, hd, dz0, dz1, 'body.door'),
    box(-dx, -dx + STILE, shell, hd, dz0, dz1, 'body.door'),
    box(-dx + STILE, dx - STILE, shell, hd, dz1 - RAIL, dz1, 'body.door'),
    box(-dx + STILE, dx - STILE, shell, hd, dz0, dz0 + RAIL, 'body.door'),
    box(-dx + STILE, dx - STILE, hd - 12, hd - 8, dz0 + RAIL, dz1 - RAIL, 'glass'),
  );
  // The lever handle on the free edge, the hinges on the hinged one.
  const hx = -dx + STILE / 2;
  solids.push(
    box(hx - 10, hx + 10, hd, hd + 8, h / 2 - 90, h / 2 + 90, 'handle'),
    box(hx - 7, hx + 7, hd + 8, hd + 28, h / 2 - 70, h / 2 + 70, 'handle'),
  );
  for (const z of [180, h / 2, h - 180]) solids.push({ kind: 'cyl', role: 'hinges', axis: 'z', at: [hw - 2, hd - 6], r: 7, from: z - 35, to: z + 35 });
  // The lifting brackets, one at each corner of the roof.
  for (const [sx, sy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
    const x0 = sx > 0 ? hw - 70 : -hw + 20, y0 = sy > 0 ? shell - 50 : -hd + 20;
    solids.push(box(x0, x0 + 50, y0, y0 + 30, h, h + 10, 'brackets'));
  }
  return turnModel3d({
    size: SPA_MANAGER_SIZE,
    solids: [...plinth(hw, hd), ...solids.map((s) => lift(s, KS714_PLINTH))],
    colours: { body: KS714_COLOUR, plinth: '#232527', glass: '#b9d7e3', plates: '#dfe2de', devices: '#f1f1ee', toggles: '#3a3f45', handle: '#26292d', hinges: '#26292d', brackets: '#3a3f45' },
    metal: ['hinges'],
    glass: ['glass'],
  });
}

const rect = (x0: number, x1: number, y0: number, y1: number): Vec2[] => [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];

/** The plan symbol: the cabinet over its footprint, its door across the front
 *  (−d/2 its back, against the wall) and the door's swing: the leaf opened a
 *  quarter about its hinges (+x) and the arc its free edge sweeps, so the
 *  space it needs in front shows. Bounds are its footprint. */
export function ks714Geometry(color = KS714_COLOUR): Geometry {
  const [w, d] = KS714_SIZE, hw = w / 2, hd = d / 2, leaf = w - 2 * SHEET;
  const ink = '#3a3f45', swing = '#7a828c';
  const outline = rect(-hw, hw, -hd, hd);
  const entities: GeomEntity[] = [
    { kind: 'hatch', loops: [outline], solid: true, color: `${color}e6` },
    { kind: 'polyline', points: rect(-hw, hw, -hd, hd), closed: true, color: ink },
    { kind: 'line', a: { x: -hw, y: hd - DOOR }, b: { x: hw, y: hd - DOOR }, color: ink },
    { kind: 'polyline', points: rect(hw - DOOR, hw, hd, hd + leaf), closed: true, color: swing },
    { kind: 'arc', center: { x: hw, y: hd }, radius: leaf, startAngle: 90, endAngle: 180, color: swing },
  ];
  return turnGeometry({ entities, bounds: { min: { x: -hw, y: -hd }, max: { x: hw, y: hd } } });
}
